/**
 * AnalyticsSetup
 *
 * Initializes PostHog and keeps user identity super properties in sync.
 * Mount once near the app root, inside the Apollo provider so useCurrent works.
 */
import { useRouter } from 'next/router';
import * as React from 'react';

import {
  identifyUser,
  initPostHog,
  posthog,
  session_ended,
  session_started,
  usePageContext,
} from '@/analytics';
import { isAdminAnalyticsPath } from '@/analytics/routePolicy';
import { useAnalyticsUserProperties } from '@/hooks/useAnalyticsUserProperties';
import { useCurrent } from '@/hooks/useCurrent';

export const AnalyticsSetup: React.FC = ({ children }) => {
  const { properties } = useAnalyticsUserProperties();
  const current = useCurrent();
  const router = useRouter();
  const analyticsDisabled = isAdminAnalyticsPath(router.pathname);

  React.useEffect(() => {
    if (analyticsDisabled) return;
    initPostHog();
  }, [analyticsDisabled]);

  React.useEffect(() => {
    if (analyticsDisabled || !properties.currentUserId) return;

    identifyUser({
      userId: properties.currentUserId,
      tenantId: current.tenant?._id ?? null,
      associationCode: properties.associationCode ?? null,
      tenantName: current.tenant?.displayName ?? null,
      tenantAcronym: current.tenant?.acronym ?? null,
      role: properties.role ?? null,
      analyticsRoleType: properties.role ?? null,
      userType: properties.userType ?? null,
      analyticsUserType: properties.userType ?? null,
      displayName: properties.displayName ?? null,
      firstName: properties.firstName ?? null,
      lastName: properties.lastName ?? null,
      email: properties.email ?? null,
      integrationId: properties.integrationId ?? null,
      membershipStatus: properties.membershipStatus ?? null,
      isMember: properties.isMember,
      platformIsMember: properties.platformIsMember,
      communityAccess: properties.communityAccess,
      hasOrganization: !!current.user?.rosterOrganization?._id,
      accountStatus: current.user?.accountStatus ?? null,
      integrationIsMember: properties.integrationIsMember,
    });
  }, [
    properties.currentUserId,
    properties.associationCode,
    properties.role,
    properties.userType,
    properties.displayName,
    properties.firstName,
    properties.lastName,
    properties.email,
    properties.membershipStatus,
    properties.integrationId,
    properties.integrationIsMember,
    properties.platformIsMember,
    properties.isMember,
    properties.communityAccess,
    current.tenant?._id,
    current.tenant?.displayName,
    current.tenant?.acronym,
    current.user?.accountStatus,
    current.user?.rosterOrganization?._id,
    analyticsDisabled,
  ]);

  React.useEffect(() => {
    if (
      analyticsDisabled ||
      !properties.currentUserId ||
      typeof window === 'undefined'
    )
      return;

    try {
      const sessionId = posthog.get_session_id();
      if (!sessionId) return;
      const trackedKey = `tw_analytics_session_started:${sessionId}`;
      const startKey = `tw_analytics_session_start:${sessionId}`;
      const hasPreviousSession = localStorage.getItem(
        'tw_analytics_has_previous_session'
      );

      if (!sessionStorage.getItem(trackedKey)) {
        const startedAt = Date.now();
        sessionStorage.setItem(trackedKey, 'true');
        sessionStorage.setItem(startKey, startedAt.toString());
        localStorage.setItem('tw_analytics_has_previous_session', 'true');
        session_started({
          entity_id: sessionId,
          entity_type: 'session',
          session_is_first: !hasPreviousSession,
          session_device_type: /Mobi|Android/i.test(navigator.userAgent)
            ? 'mobile'
            : 'desktop',
        });
      }

      const handlePageHide = (): void => {
        const startedAt = Number(
          sessionStorage.getItem(startKey) ?? Date.now()
        );
        session_ended({
          entity_id: sessionId,
          entity_type: 'session',
          session_duration_seconds: Math.max(
            0,
            Math.round((Date.now() - startedAt) / 1000)
          ),
          session_end_method: 'page_hide',
        });
      };
      window.addEventListener('pagehide', handlePageHide, { once: true });
      return () => window.removeEventListener('pagehide', handlePageHide);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Analytics] Session tracking unavailable', error);
      }
      return;
    }
  }, [analyticsDisabled, properties.currentUserId]);

  usePageContext(analyticsDisabled);

  return <>{children}</>;
};
